import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Calculator, Truck, Award, Zap, IndianRupee, Plus, Trash2, Download, Upload, ArrowRight, Sparkles, MapPin, Package } from 'lucide-react';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { RouteMapCard, RoutePoint } from '@/components/ui/RouteMap';
import { useGeocode } from '@/hooks/useGeocode';
import { useAuth } from '@/hooks/useAuth';

/** Route map (Pickup → Destination) driven by the from/to pincodes on the calculator form. */
function RouteMapForPincodes({ fromPincode, toPincode }: { fromPincode?: string; toPincode?: string }) {
  const geo = useGeocode([fromPincode, toPincode]);
  const validFrom = /^\d{6}$/.test(String(fromPincode ?? ''));
  const validTo = /^\d{6}$/.test(String(toPincode ?? ''));
  if (!validFrom && !validTo) return null;
  const from = geo.get(fromPincode);
  const to = geo.get(toPincode);
  const points: RoutePoint[] = [];
  if (from) points.push({ lat: from.lat!, lng: from.lng!, kind: 'from', title: `Pickup — ${fromPincode}`, subtitle: [from.city, from.state].filter(Boolean).join(', ') || undefined });
  if (to) points.push({ lat: to.lat!, lng: to.lng!, kind: 'to', title: `Destination — ${toPincode}`, subtitle: [to.city, to.state].filter(Boolean).join(', ') || undefined });
  return (
    <div className="mt-4">
      <RouteMapCard points={points} title="Pickup → Destination" loading={geo.isLoading}
        unavailableText="Enter valid 6-digit pincodes to see the route" previewHeight={160} />
    </div>
  );
}

type Quote = {
  courierId: number;
  courierName: string;
  courierLogo?: string;
  zone: string;
  chargedWeight: number;
  volumetricWeight: number;
  forwardBase: number;
  forwardAdditional: number;
  fuelSurcharge: number;
  codCharge: number;
  insurance?: number;
  oda?: number;
  fmPickup?: number;
  otherHandling?: number;
  toPay?: number;
  greenTax?: number;
  subtotal: number;
  gst: number;
  total: number;
  tatDays?: number;
  minDays?: number;
  maxDays?: number;
  tatRange?: string;
  zoneTat?: string;
  etaDate?: string;
  isCheapest?: boolean;
  isFastest?: boolean;
};

type QuoteResponse = { items: Quote[]; meta: any };

export function PriceCalculatorPage() {
  const [tab, setTab] = useState<'B2C' | 'B2B'>('B2C');
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Calculator size={22} className="text-emerald-700" /> Price Calculator
        </h1>
        {/* Bulk upload of a price calculator is an admin/internal tool — hidden from the merchant portal. */}
        {!isMerchant && (
          <div className="flex gap-2">
            <button className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1"><Upload size={14} /> Upload PriceCalculator</button>
            <button className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1"><Download size={14} /> Export PriceCalculator[s]</button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['B2C', 'B2B'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${tab === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === 'B2C' ? <B2CCalculator /> : <B2BCalculator />}
    </div>
  );
}

function B2CCalculator() {
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  const selfMerchantId = isMerchant ? Number(user!.id) : '';
  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-pc'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
    enabled: !isMerchant,
  });
  const merchantList = useMemo(() => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []), [merchants]);

  const [form, setForm] = useState<any>({
    merchantId: selfMerchantId,
    fromPincode: '',
    toPincode: '',
    deliveryType: 'PREPAID',
    invoiceValue: '',
    weight: '',
    length: '',
    breadth: '',
    height: '',
  });

  const quote = useMutation({
    mutationFn: async () => (await api.post<QuoteResponse>('/price-calculator/b2c', form)).data,
  });

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-7 bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Package size={18} className="text-emerald-700" />
          <h3 className="font-semibold text-slate-900">Shipment Details</h3>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {!isMerchant && (
            <Field label="Select Merchant *">
              <SearchableSelect value={form.merchantId}
                onChange={(v) => setForm({ ...form, merchantId: v === '' ? '' : Number(v) })}
                options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}` }))}
                placeholder="Search & choose merchant" />
            </Field>
          )}
          <Field label="Delivery Type">
            <select value={form.deliveryType} onChange={(e) => setForm({ ...form, deliveryType: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="PREPAID">Prepaid</option>
              <option value="COD">COD</option>
            </select>
          </Field>
          <Field label="Pickup Pincode *">
            <input value={form.fromPincode} onChange={(e) => setForm({ ...form, fromPincode: e.target.value })} placeholder="201301" className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Destination Pincode *">
            <input value={form.toPincode} onChange={(e) => setForm({ ...form, toPincode: e.target.value })} placeholder="560001" className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Invoice Value (₹)">
            <input type="number" value={form.invoiceValue} onChange={(e) => setForm({ ...form, invoiceValue: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Total Weight (kg) *">
            <input type="number" step="0.01" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Length (cm)"><input type="number" value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Breadth (cm)"><input type="number" value={form.breadth} onChange={(e) => setForm({ ...form, breadth: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Height (cm)"><input type="number" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
        </div>
        {form.length && form.breadth && form.height && (
          <div className="mt-3 p-2 bg-blue-50 text-blue-700 rounded text-xs flex items-center gap-2">
            <Sparkles size={12} /> Volumetric weight: <b>{((Number(form.length) * Number(form.breadth) * Number(form.height)) / 5000).toFixed(2)} kg</b> · Chargeable: <b>{Math.max(Number(form.weight ?? 0), (Number(form.length) * Number(form.breadth) * Number(form.height)) / 5000).toFixed(2)} kg</b>
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={() => quote.mutate()} disabled={quote.isPending || !form.merchantId || !form.fromPincode || !form.toPincode || !form.weight}
            className="bg-emerald-600 text-white px-5 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-50">
            <Calculator size={14} /> {quote.isPending ? 'Calculating…' : 'Calculate'}
          </button>
          <button onClick={() => setForm({ merchantId: selfMerchantId, fromPincode: '', toPincode: '', deliveryType: 'PREPAID', invoiceValue: '', weight: '', length: '', breadth: '', height: '' })} className="border border-red-300 text-red-600 px-4 py-2 rounded text-sm">RESET</button>
        </div>
      </div>
      <div className="col-span-5">
        <Results data={quote.data} loading={quote.isPending} />
        <RouteMapForPincodes fromPincode={form.fromPincode} toPincode={form.toPincode} />
      </div>
    </div>
  );
}

function B2BCalculator() {
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  const selfMerchantId = isMerchant ? Number(user!.id) : '';
  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-pc-b2b'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
    enabled: !isMerchant,
  });
  const merchantList = useMemo(() => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []), [merchants]);
  const { data: couriers } = useQuery({
    queryKey: ['couriers-light-pc'],
    queryFn: async () => (await api.get<any[]>('/couriers', { params: { isActive: 'true' } })).data,
    enabled: !isMerchant, // merchants pick "All assigned"; the backend scopes to their active assigned couriers
  });

  const [form, setForm] = useState<any>({
    merchantId: selfMerchantId, courierId: '', fromPincode: '', toPincode: '', weight: '',
    deliveryType: 'PREPAID', codValue: '', invoiceValue: '',
    insuranceType: 'OWNER', pickupType: 'SELF_DROP',
    boxes: [{ qty: 1, length: '', breadth: '', height: '' }],
  });
  const addBox = () => setForm({ ...form, boxes: [...form.boxes, { qty: 1, length: '', breadth: '', height: '' }] });
  const setBox = (i: number, k: string, v: any) => setForm({ ...form, boxes: form.boxes.map((b: any, idx: number) => idx === i ? { ...b, [k]: v } : b) });
  const rmBox = (i: number) => setForm({ ...form, boxes: form.boxes.filter((_: any, idx: number) => idx !== i) });

  const quote = useMutation({
    mutationFn: async () => (await api.post<QuoteResponse>('/price-calculator/b2b', form)).data,
  });

  const totalVol = form.boxes.reduce((s: number, b: any) => {
    const l = Number(b.length || 0), br = Number(b.breadth || 0), h = Number(b.height || 0), q = Number(b.qty || 1);
    return s + (l > 0 && br > 0 && h > 0 ? ((l * br * h) / 4500) * q : 0);
  }, 0);

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-7 bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><Package size={18} className="text-emerald-700" /><h3 className="font-semibold">B2B Shipment Details</h3></div>
        <div className="grid grid-cols-3 gap-3">
          {!isMerchant && (
            <Field label="Select Merchant *">
              <SearchableSelect value={form.merchantId}
                onChange={(v) => setForm({ ...form, merchantId: v === '' ? '' : Number(v) })}
                options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}` }))}
                placeholder="Search & choose merchant" />
            </Field>
          )}
          <Field label="Select Courier">
            <select value={form.courierId} onChange={(e) => setForm({ ...form, courierId: e.target.value ? Number(e.target.value) : '' })} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">All assigned</option>
              {(couriers ?? []).filter((c: any) => c.isActive !== false).map((c: any) => <option key={c.id} value={c.id}>#{c.id} · {c.name}</option>)}
            </select>
          </Field>
          <Field label="Pickup Pincode *"><input value={form.fromPincode} onChange={(e) => setForm({ ...form, fromPincode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Destination Pincode *"><input value={form.toPincode} onChange={(e) => setForm({ ...form, toPincode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Total Weight (kg) *"><input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Delivery Type">
            <select value={form.deliveryType} onChange={(e) => setForm({ ...form, deliveryType: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="PREPAID">Prepaid</option>
              <option value="COD">COD</option>
            </select>
          </Field>
          <Field label="COD Amount (₹)"><input type="number" value={form.codValue} onChange={(e) => setForm({ ...form, codValue: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Product Invoice Value (₹)"><input type="number" value={form.invoiceValue} onChange={(e) => setForm({ ...form, invoiceValue: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <RadioGroup label="Freight Mode" value={form.deliveryType} onChange={(v) => setForm({ ...form, deliveryType: v })} options={[{value:'COD', label:'Collect on Delivery'},{value:'PREPAID', label:'Bill to Client'}]} />
          <RadioGroup label="Insurance Type" value={form.insuranceType} onChange={(v) => setForm({ ...form, insuranceType: v })} options={[{value:'OWNER', label:'Owner Risk'},{value:'CARRIER', label:'Carrier Risk'}]} />
          <RadioGroup label="Pickup Type" value={form.pickupType} onChange={(v) => setForm({ ...form, pickupType: v })} options={[{value:'SELF_DROP', label:'Self Drop'},{value:'FM_PICKUP', label:'FM Pickup'}]} />
        </div>
        <div className="mt-4">
          <div className="text-sm font-medium text-slate-700 mb-2">Package Dimensions</div>
          {form.boxes.map((b: any, i: number) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
              <div className="col-span-2"><Field label="Qty"><input type="number" value={b.qty} onChange={(e) => setBox(i, 'qty', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></Field></div>
              <div className="col-span-3"><Field label="Length (cm)"><input type="number" value={b.length} onChange={(e) => setBox(i, 'length', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></Field></div>
              <div className="col-span-3"><Field label="Breadth (cm)"><input type="number" value={b.breadth} onChange={(e) => setBox(i, 'breadth', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></Field></div>
              <div className="col-span-3"><Field label="Height (cm)"><input type="number" value={b.height} onChange={(e) => setBox(i, 'height', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" /></Field></div>
              <div className="col-span-1">{form.boxes.length > 1 && <button onClick={() => rmBox(i)} className="border border-red-300 text-red-600 p-1.5 rounded"><Trash2 size={14} /></button>}</div>
            </div>
          ))}
          <button onClick={addBox} className="text-emerald-700 border border-emerald-300 px-3 py-1 rounded text-xs flex items-center gap-1"><Plus size={12} /> ADD MORE</button>
        </div>
        {totalVol > 0 && (
          <div className="mt-3 p-2 bg-blue-50 text-blue-700 rounded text-xs flex items-center gap-2">
            <Sparkles size={12} /> Volumetric weight: <b>{totalVol.toFixed(2)} kg</b> · Chargeable: <b>{Math.max(Number(form.weight ?? 0), totalVol).toFixed(2)} kg</b>
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={() => quote.mutate()} disabled={quote.isPending || !form.merchantId || !form.fromPincode || !form.toPincode}
            className="bg-emerald-600 text-white px-5 py-2 rounded text-sm flex items-center gap-1 disabled:opacity-50">
            <Calculator size={14} /> {quote.isPending ? 'Calculating…' : 'Calculate'}
          </button>
          <button onClick={() => setForm({ merchantId: selfMerchantId, courierId: '', fromPincode: '', toPincode: '', weight: '', deliveryType: 'PREPAID', codValue: '', invoiceValue: '', insuranceType: 'OWNER', pickupType: 'SELF_DROP', boxes: [{ qty: 1, length: '', breadth: '', height: '' }] })} className="border border-red-300 text-red-600 px-4 py-2 rounded text-sm">RESET</button>
        </div>
      </div>
      <div className="col-span-5">
        <Results data={quote.data} loading={quote.isPending} variant="b2b" />
        <RouteMapForPincodes fromPincode={form.fromPincode} toPincode={form.toPincode} />
      </div>
    </div>
  );
}

function Results({ data, loading, variant }: { data?: QuoteResponse; loading: boolean; variant?: 'b2c' | 'b2b' }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 sticky top-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2"><IndianRupee size={16} /> Freight Breakup</h3>
        {data?.meta?.zoneName && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1"><MapPin size={10} /> {data.meta.zoneName}</span>}
      </div>
      {loading ? (
        <div className="text-center text-slate-400 py-12">Calculating across couriers…</div>
      ) : !data ? (
        <div className="text-center text-slate-400 py-12 text-sm">Fill the form → click <b>Calculate</b> to see prices for every courier the merchant has access to.</div>
      ) : data.items.length === 0 ? (
        <div className="text-center text-slate-400 py-12 text-sm">No couriers returned a price. Check the merchant's pricing plan + assigned couriers.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="Cheapest" value={`₹${data.meta.cheapest?.toFixed(2)}`} accent="emerald" />
            <Stat label="Couriers" value={String(data.items.length)} />
            <Stat label="Savings vs Max" value={`₹${data.meta.savingsVsMax?.toFixed(2) ?? '0'}`} accent="amber" />
          </div>
          <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
            {data.items.map((q) => (
              <div key={q.courierId} className={`p-3 rounded-lg border ${q.isCheapest ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center"><Truck size={14} /></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      {q.courierName}
                      {q.isCheapest && <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1"><Award size={10} /> Best Price</span>}
                      {q.isFastest && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1"><Zap size={10} /> Fastest</span>}
                    </div>
                    <div className="text-xs text-slate-500">{q.chargedWeight.toFixed(2)} kg · {q.tatRange ? `EDD ${q.etaDate} (${q.tatRange})` : 'EDD —'}</div>
                  </div>
                  <div className="text-right">
                    {/* The quoted total is what will actually be billed — whole rupees, like the freight row. */}
                    <div className="text-lg font-bold text-slate-900">₹{Math.round(q.total).toLocaleString('en-IN')}</div>
                    <div className="text-[10px] text-slate-400">incl. GST</div>
                  </div>
                </div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-slate-500 hover:text-emerald-700">Breakdown</summary>
                  <div className="mt-2 text-xs text-slate-600 grid grid-cols-2 gap-x-3 gap-y-1">
                    <div>Base: ₹{q.forwardBase.toFixed(2)}</div>
                    {q.forwardAdditional > 0 && <div>Additional: ₹{q.forwardAdditional.toFixed(2)}</div>}
                    {q.fuelSurcharge > 0 && <div>Fuel: ₹{q.fuelSurcharge.toFixed(2)}</div>}
                    {q.codCharge > 0 && <div>COD: ₹{q.codCharge.toFixed(2)}</div>}
                    {variant === 'b2b' && q.insurance !== undefined && q.insurance > 0 && <div>Insurance: ₹{q.insurance.toFixed(2)}</div>}
                    {variant === 'b2b' && q.oda !== undefined && q.oda > 0 && <div>ODA: ₹{q.oda.toFixed(2)}</div>}
                    {variant === 'b2b' && q.fmPickup !== undefined && q.fmPickup > 0 && <div>FM Pickup: ₹{q.fmPickup.toFixed(2)}</div>}
                    {variant === 'b2b' && q.greenTax !== undefined && q.greenTax > 0 && <div>Green Tax: ₹{q.greenTax.toFixed(2)}</div>}
                    <div>Subtotal: ₹{q.subtotal.toFixed(2)}</div>
                    <div>GST 18%: ₹{q.gst.toFixed(2)}</div>
                  </div>
                </details>
                <button className="mt-2 text-xs text-emerald-700 hover:underline flex items-center gap-1">
                  Book with this courier <ArrowRight size={11} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  const cls = accent === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : accent === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`p-2 rounded border ${cls}`}>
      <div className="text-[10px] uppercase">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-xs text-gray-500 mb-1">{label}</label>{children}</div>;
}

function RadioGroup({ label, value, onChange, options }: { label: string; value: string; onChange: (v: any) => void; options: {value:string;label:string}[] }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <div className="flex flex-col gap-1">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <input type="radio" name={label} checked={value === o.value} onChange={() => onChange(o.value)} /> {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}
